/**
 * Global Toast notification system
 *
 * Usage from any page/component:
 *   import { showToast } from './Toast';
 *   showToast('Guardado correctamente');          // success (default)
 *   showToast('Error al guardar', 'error');
 *   showToast('Procesando...', 'info');
 */

import { useState, useEffect } from 'react';
import { CheckCircle2, XCircle, Info, X } from 'lucide-react';

// Fire this from anywhere in the app
export function showToast(message, type = 'success') {
  window.dispatchEvent(new CustomEvent('app:toast', { detail: { message, type } }));
}

const ICONS = {
  success: <CheckCircle2 size={16} />,
  error:   <XCircle size={16} />,
  info:    <Info size={16} />,
};

const COLORS = {
  success: { bg: '#10b981', border: '#059669' },
  error:   { bg: '#ef4444', border: '#dc2626' },
  info:    { bg: '#3b82f6', border: '#2563eb' },
};

let uid = 0;

export default function Toast() {
  const [toasts, setToasts] = useState([]);

  useEffect(() => {
    const handler = (e) => {
      const id = ++uid;
      setToasts(prev => [...prev, { id, ...e.detail }]);
      setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 3200);
    };
    window.addEventListener('app:toast', handler);
    return () => window.removeEventListener('app:toast', handler);
  }, []);

  if (!toasts.length) return null;

  return (
    <div style={{
      position: 'fixed', bottom: '5rem', right: '1rem', zIndex: 9000,
      display: 'flex', flexDirection: 'column-reverse', gap: '0.5rem',
      pointerEvents: 'none',
    }}>
      {toasts.map(({ id, message, type }) => {
        const { bg, border } = COLORS[type] || COLORS.success;
        return (
          <div key={id} style={{
            display: 'flex', alignItems: 'center', gap: '0.5rem',
            background: bg, color: '#fff',
            padding: '0.625rem 0.875rem',
            borderRadius: '0.625rem',
            boxShadow: `0 4px 16px rgba(0,0,0,0.2), 0 0 0 1px ${border}`,
            fontSize: '0.875rem', fontWeight: 600,
            minWidth: '180px', maxWidth: '280px',
            pointerEvents: 'auto',
            animation: 'toastIn 0.2s ease',
          }}>
            {ICONS[type] || ICONS.success}
            <span style={{ flex: 1 }}>{message}</span>
            <button
              onClick={() => setToasts(prev => prev.filter(t => t.id !== id))}
              style={{ background: 'none', border: 'none', color: '#fff', cursor: 'pointer', padding: '0.1rem', display: 'flex', opacity: 0.8, flexShrink: 0 }}
            >
              <X size={14} />
            </button>
          </div>
        );
      })}
    </div>
  );
}
